// useJanusAudioRoom.js
import { useEffect, useRef, useState } from 'react';
import { getRecoil, setRecoil } from 'recoil-nexus';
import {
  INTERPHONE_STATUS_CALLING,
  INTERPHONE_STATUS_CANCEL,
  INTERPHONE_STATUS_NONE,
} from '../../recoil/area';
import { fcmInterphoneState } from '../../recoil/fcmMessage';
import Janus from './Janus';
import { CALL_TYPE_RECEIVER, CALL_TYPE_SENDER } from '../../controller/InterphoneController';

const server = process.env.REACT_APP_JANUS_URL || 'http://localhost:3000/janus';
const opaqueId = 'audioroom-' + Janus.randomString(12);

export default function useJanusAudioRoom({ roomId, canJoin, sendType }) {
  const janusRef = useRef(null);
  const vroomHandleRef = useRef(null);
  const subscriberHandleRef = useRef(null);
  const mypvtidRef = useRef(null);
  const [joined, setJoined] = useState(true);
  const [error, setError] = useState(null);

  // 방 나가기
  const leaveRoom = () => {
    console.log('leave room try');

    if (vroomHandleRef.current) {
      console.log('publisher leave room');

      try {
        vroomHandleRef.current.send({ message: { request: 'leave' } });
        // vroomHandleRef.current.hangup();
        vroomHandleRef.current.detach();
        setRecoil(fcmInterphoneState, (prev) => ({
          ...prev,
          status: INTERPHONE_STATUS_NONE,
        }));
      } catch (err) {
        console.warn('Error while leaving room:', err);
      }
    }
    if (subscriberHandleRef.current) {
      console.log('subscriber leave room');
      try {
        subscriberHandleRef.current.send({ message: { request: 'leave' } });
        subscriberHandleRef.current.hangup();
      } catch (err) {
        console.warn('Error while leaving subscriber:', err);
      }
    }
    if (janusRef.current) {
      janusRef.current.destroy(() => {
        console.log('Janus session destroyed.');
      });
    }
    setJoined(false);
    setError(null);
  };

  useEffect(() => {
    if (!roomId) {
      console.warn('no roomId');

      return;
    }

    if (!canJoin) {
      console.warn('join impossible');
      leaveRoom();
      return;
    }

    if (sendType === '') {
      console.warn('no sendType');
      return;
    }

    console.log('room info', roomId, canJoin, getRecoil(fcmInterphoneState));

    Janus.init({
      debug: 'all',
      callback: () => {
        if (janusRef.current) {
          console.warn('already connected');
          return;
        } // 이미 연결되어 있으면 중복 방지
        janusRef.current = new Janus({
          server,
          iceServers: [
            { urls: process.env.REACT_APP_JANUS_STUN_URL },
            {
              urls: process.env.REACT_APP_JANUS_TURN_URL,
              username: 'janustest',
              credential: 'janustest',
            },
          ],
          success: () => {
            janusRef.current.attach({
              plugin: 'janus.plugin.videoroom',
              opaqueId,
              success: (pluginHandle) => {
                vroomHandleRef.current = pluginHandle;
                const joinMsg = {
                  request: 'join',
                  room: roomId,
                  ptype: 'publisher',
                  display: sendType,
                };
                console.log('joinMsg', joinMsg);
                pluginHandle.send({ message: joinMsg });
              },
              error: (err) => {
                setError('플러그인 연결 실패: ' + err);
                console.warn('플러그인 연결 실패: ' + err);
                setRecoil(fcmInterphoneState, (prev) => ({
                  ...prev,
                  status: INTERPHONE_STATUS_CANCEL,
                }));
              },
              consentDialog: (on) => {
                console.log('Microphone consent dialog should', on ? 'appear' : 'disappear');
              },
              onmessage: (msg, jsep) => {
                const event = msg['videoroom'];

                // 연결 시작
                if (event === 'joined') {
                  // if (msg['publishers'] && msg['publishers'].length >= 1) {
                  //   console.warn('상대방이 통화 중입니다.');
                  //   setError('상대방이 통화 중입니다.');
                  //   leaveRoom();
                  //   return;
                  // }
                  mypvtidRef.current = msg['private_id'];
                  publishAudio();

                  if (msg['publishers'] && msg['publishers'].length > 0) {
                    const publisher = msg['publishers'][0];
                    if (publisher.display === CALL_TYPE_SENDER) {
                      subscribeToFeed(publisher.id);
                    }
                  }
                }
                if (event === 'event' && msg['publishers'] && msg['publishers'].length > 0) {
                  const publisher = msg['publishers'][0];
                  subscribeToFeed(publisher.id);
                }
                // 연결 종료
                if (event === 'destroyed') {
                  console.warn('통화가 종료되었어요');
                  setError('통화가 종료되었어요.');
                  leaveRoom();
                  return;
                }
                if (jsep) {
                  vroomHandleRef.current?.handleRemoteJsep({ jsep });
                }
              },
              // 녹음 권한 받으면 통화중으로 변경
              onlocaltrack: (track, on) => {
                if (!on || track.kind !== 'audio') return;
                console.log('Got local audio track:', track);
                setJoined(true);
                if (sendType === CALL_TYPE_RECEIVER) {
                  setRecoil(fcmInterphoneState, (prev) => ({
                    ...prev,
                    status: INTERPHONE_STATUS_CALLING,
                  }));
                }
              },
              oncleanup: () => {},
            });
          },
          error: (err) => {
            console.warn('Janus 세션 오류: ' + err);
            setError('Janus 세션 오류: ' + err);
            // setRecoil(fcmInterphoneState, (prev) => ({
            //   ...prev,
            //   status: INTERPHONE_STATUS_CANCEL,
            // }));
            leaveRoom();
          },
          hangup: () => {
            console.warn('janus hangup');
          },
          destroyed: () => {
            console.warn('janus destroyed');
          },
        });
      },
    });

    function publishAudio() {
      vroomHandleRef.current?.createOffer({
        media: { audioRecv: true, videoRecv: false, audioSend: true, videoSend: false },
        success: (jsep) => {
          console.warn('join success');
          const publish = { request: 'configure', audio: true, video: false };
          vroomHandleRef.current?.send({ message: publish, jsep });
        },
        error: (err) => {
          console.warn('오디오 발행 실패: ' + err);
          setError('오디오 발행 실패: ' + err);
          setRecoil(fcmInterphoneState, (prev) => ({
            ...prev,
            status: INTERPHONE_STATUS_CANCEL,
          }));
        },
      });
    }

    function subscribeToFeed(feedId) {
      console.log('=== subscribe try ===');
      janusRef.current.attach({
        plugin: 'janus.plugin.videoroom',
        opaqueId,
        success: (subscriberPluginHandle) => {
          subscriberHandleRef.current = subscriberPluginHandle;
          const subscribe = {
            request: 'join',
            room: roomId,
            ptype: 'subscriber',
            feed: feedId,
            private_id: mypvtidRef.current,
          };
          subscriberPluginHandle.send({ message: subscribe });

          console.log('=== subscribe success!! ===');
        },
        onmessage: (msg, jsep) => {
          if (jsep && subscriberHandleRef.current) {
            subscriberHandleRef.current.createAnswer({
              jsep,
              media: { audioSend: false, videoSend: false },
              success: (jsepAnswer) => {
                const body = { request: 'start', room: roomId };
                subscriberHandleRef.current.send({ message: body, jsep: jsepAnswer });
              },
              error: (err) => {
                console.error('Subscriber WebRTC error:', err);
              },
            });
          }
        },
        onremotetrack: (track, mid, on) => {
          if (!on || track.kind !== 'audio') return;
          console.log('Remote audio track:', track);
          const stream = new MediaStream([track]);
          const audioEl = new Audio();
          audioEl.srcObject = stream;
          audioEl.autoplay = true;
          audioEl.playsInline = true;
          audioEl.style.display = 'none';
          document.body.appendChild(audioEl);
        },
        error: (err) => {
          console.error('Failed to attach subscriber', err);
        },
      });
    }

    return () => {
      setError('cleanup.');
      // leaveRoom();
    };
  }, [roomId, canJoin, sendType]);

  return { joined, error, leaveRoom };
}
